import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Check, Edit2, Archive, RotateCw, Mail } from 'lucide-react';

import { api } from '../../api/client.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { SingleSelect, MultiSelect } from '../DropdownField.jsx';
import './StartupsPanel.css';

export function StartupsPanel() {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const isClient = user?.role === 'client';
    const [creating, setCreating] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    const { data: startups, isLoading, error } = useQuery({
        queryKey: ['startups-full'],
        queryFn: () => api.get('/startups?include_inactive=true'),
    });

    const createMutation = useMutation({
        mutationFn: (data) => api.post('/startups', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['startups-full'] });
            queryClient.invalidateQueries({ queryKey: ['startups'] });
            setCreating(false);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, updates }) => api.put(`/startups/${id}`, updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['startups-full'] });
            queryClient.invalidateQueries({ queryKey: ['startups'] });
            setEditingId(null);
        },
    });

    const deactivateMutation = useMutation({
        mutationFn: (id) => api.delete(`/startups/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['startups-full'] }),
    });

    if (isLoading) return <div className="panel-empty"><span className="spinner" /></div>;
    if (error) return <div className="panel-error">Couldn't load startups: {error.message}</div>;

    const active   = (startups || []).filter(s => s.is_active);
    const inactive = (startups || []).filter(s => !s.is_active);

    return (
        <div>
            <div className="panel-section">
                <div className="panel-section-header">
                    <div>
                        <h2 className="panel-section-title">
                            {isClient ? 'Your portfolio company' : 'Portfolio companies'}
                        </h2>
                        <p className="panel-section-subtitle">
                            {isClient
                                ? 'Edit your profile so the matchmaker can find the right investors for you.'
                                : <>{active.length} active{inactive.length > 0 && ` · ${inactive.length} archived`}</>}
                        </p>
                    </div>
                    {!creating && !isClient && (
                        <button className="btn-primary-small" onClick={() => setCreating(true)}>
                            <Plus size={14} strokeWidth={2} /> Add startup
                        </button>
                    )}
                </div>

                {creating && !isClient && (
                    <StartupForm
                        onCancel={() => setCreating(false)}
                        onSubmit={(data) => createMutation.mutate(data)}
                        submitting={createMutation.isPending}
                        error={createMutation.error}
                    />
                )}

                <ul className="startup-list">
                    {active.map(s => (
                        <StartupRow
                            key={s.id}
                            startup={s}
                            editing={editingId === s.id}
                            expanded={expandedId === s.id}
                            onEdit={() => setEditingId(s.id)}
                            onCancel={() => setEditingId(null)}
                            onSave={(updates) => updateMutation.mutate({ id: s.id, updates })}
                            onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)}
                            canArchive={!isClient}
                            onArchive={() => {
                                if (confirm(`Archive ${s.name}? Investors will stay assigned, but the startup won't appear in active lists.`)) {
                                    deactivateMutation.mutate(s.id);
                                }
                            }}
                            saving={updateMutation.isPending && updateMutation.variables?.id === s.id}
                        />
                    ))}
                </ul>
            </div>

            {/* Hide the "Archived" section for clients — they don't need to see archives */}
            {!isClient && inactive.length > 0 && (
                <div className="panel-section">
                    <h2 className="panel-section-title">Archived ({inactive.length})</h2>
                    <ul className="startup-list">
                        {inactive.map(s => (
                            <li key={s.id} className="startup-row startup-row-inactive">
                                <div className="startup-info">
                                    <div className="startup-name">{s.name}</div>
                                    {s.sector && <div className="startup-meta">{s.sector}</div>}
                                </div>
                                <button
                                    className="user-action-btn"
                                    title="Restore"
                                    onClick={() => updateMutation.mutate({ id: s.id, updates: { is_active: 1 } })}
                                >
                                    <RotateCw size={13} strokeWidth={1.75} /> Restore
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
function StartupRow({ startup, editing, expanded, onEdit, onCancel, onSave,
                     onToggle, onArchive, saving, canArchive }) {
    if (editing) {
        return (
            <li className="startup-row startup-row-editing">
                <StartupForm
                    startup={startup}
                    onCancel={onCancel}
                    onSubmit={onSave}
                    submitting={saving}
                />
            </li>
        );
    }

    const hasTemplate = !!(startup.email_subject || startup.email_body);

    return (
        <li className={`startup-row${expanded ? ' is-expanded' : ''}`}>
            <div className="startup-header" onClick={onToggle}>
                <div className="startup-info">
                    <div className="startup-name">{startup.name}</div>
                    <div className="startup-meta">
                        {[startup.sector, startup.stage, startup.geo_focus]
                            .filter(Boolean).join(' · ') || 'No profile yet'}
                    </div>
                </div>

                {hasTemplate && (
                    <span className="startup-template-pill">
                        <Mail size={11} strokeWidth={1.75}/> Template
                    </span>
                )}

                <div className="startup-actions" onClick={(e) => e.stopPropagation()}>
                    <button className="user-action-btn" onClick={onEdit}>
                        <Edit2 size={13} strokeWidth={1.75}/> Edit
                    </button>
                    {canArchive && (
                        <button className="user-action-btn user-action-danger" onClick={onArchive}
                                title="Archive">
                            <Archive size={13} strokeWidth={1.75}/>
                        </button>
                    )}
                </div>
            </div>

            {expanded && (
                <div className="startup-detail">
                    {startup.description && (
                        <div className="startup-detail-block">
                            <span className="detail-field-label">Description</span>
                            <p className="startup-detail-text">{startup.description}</p>
                        </div>
                    )}
                    <div className="startup-detail-grid">
                        {startup.financing_need != null && startup.financing_need !== '' && (
                            <div className="startup-detail-block">
                                <span className="detail-field-label">Financing need</span>
                                <p className="startup-detail-text mono">
                                    {formatEuro(startup.financing_need)}
                                </p>
                            </div>
                        )}
                    </div>
                    {hasTemplate && (
                        <div className="startup-detail-block">
                            <span className="detail-field-label">Email template</span>
                            {startup.email_subject && (
                                <p className="startup-detail-text">
                                    <strong>Subject:</strong> {startup.email_subject}
                                </p>
                            )}
                            {startup.email_body && (
                                <pre className="startup-email-body">{startup.email_body}</pre>
                            )}
                        </div>
                    )}
                </div>
            )}
        </li>
    );
}

/* -------------------------------------------------------------------------- */
const TEMPLATE_VARS = [
    { label: 'First name',     placeholder: '{{contact_first_name}}' },
    { label: 'Full name',      placeholder: '{{contact_full_name}}' },
    { label: 'Investor name',  placeholder: '{{investor_name}}' },
    { label: 'My name',        placeholder: '{{my_name}}' },
];

function TemplatePills({ fieldRef, field, form, setForm }) {
    const insertVar = useCallback((placeholder) => {
        const el = fieldRef.current;
        if (!el) return;

        const start = el.selectionStart ?? el.value.length;
        const end   = el.selectionEnd   ?? el.value.length;
        const before = el.value.slice(0, start);
        const after  = el.value.slice(end);

        // Add a leading space if inserting mid-word without one
        const needsLeadingSpace  = start > 0 && !/\s$/.test(before);
        const needsTrailingSpace = after.length > 0 && !/^\s/.test(after);
        const insertion = (needsLeadingSpace ? ' ' : '') + placeholder + (needsTrailingSpace ? ' ' : '');
        const newValue  = before + insertion + after;
        const newCursor = start + insertion.length;

        setForm(prev => ({ ...prev, [field]: newValue }));

        // Restore cursor after React re-render
        requestAnimationFrame(() => {
            if (fieldRef.current) {
                fieldRef.current.focus();
                fieldRef.current.setSelectionRange(newCursor, newCursor);
            }
        });
    }, [fieldRef, field, setForm]);

    return (
        <div className="template-pills">
            {TEMPLATE_VARS.map(v => (
                <button
                    key={v.placeholder}
                    type="button"
                    className="template-pill"
                    title={v.placeholder}
                    onClick={() => insertVar(v.placeholder)}
                >
                    {v.label}
                </button>
            ))}
        </div>
    );
}

function StartupForm({ startup = {}, onCancel, onSubmit, submitting, error }) {
    const { data: settings } = useQuery({
        queryKey: ['settings'],
        queryFn: () => api.get('/settings'),
    });
    const sectorOptions    = Array.isArray(settings?.sectors)            ? settings.sectors            : [];
    const stageOptions     = Array.isArray(settings?.investment_stages)  ? settings.investment_stages  : [];
    const geoOptions       = Array.isArray(settings?.geo_options)        ? settings.geo_options        : [];
    const financingOptions = Array.isArray(settings?.financing_types)    ? settings.financing_types    : [];

    const subjectRef = useRef(null);
    const bodyRef    = useRef(null);

    const [form, setForm] = useState({
        name: startup.name || '',
        description: startup.description || '',
        sector: startup.sector || '',
        stage: startup.stage || '',
        geo_focus: startup.geo_focus || '',
        financing_need: startup.financing_need ?? '',  // single number now
        email_subject: startup.email_subject || '',
        email_body: startup.email_body || '',
    });

    const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
    const setValue = (field) => (newValue) => setForm(prev => ({ ...prev, [field]: newValue || '' }));

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {};
        for (const [k, v] of Object.entries(form)) {
            if (k === 'financing_need') {
                payload[k] = v === '' || v == null ? null : Number(v);
            } else if (typeof v === 'string') {
                payload[k] = v.trim() || null;
            } else {
                payload[k] = v;
            }
        }
        onSubmit(payload);
    };

    return (
        <form className="invite-form" onSubmit={handleSubmit} style={{ width: '100%' }}>
            <div className="invite-grid">
                <label className="detail-field">
                    <span className="detail-field-label">Name *</span>
                    <input required value={form.name} onChange={set('name')}
                           className="detail-field-input" placeholder="e.g. Sunco"/>
                </label>
                <div className="detail-field">
                    <span className="detail-field-label">Sector</span>
                    <SingleSelect value={form.sector} options={sectorOptions}
                                  onChange={setValue('sector')}
                                  placeholder="e.g. Clean energy"/>
                </div>
                <div className="detail-field">
                    <span className="detail-field-label">Stage</span>
                    <SingleSelect value={form.stage} options={stageOptions}
                                  onChange={setValue('stage')}
                                  placeholder="e.g. Seed, Series A"/>
                </div>
                <div className="detail-field">
                    <span className="detail-field-label">Geographic focus</span>
                    <MultiSelect value={form.geo_focus} options={geoOptions}
                                 onChange={setValue('geo_focus')}
                                 placeholder="e.g. Europe, LATAM"/>
                </div>
                <label className="detail-field detail-field-full">
                    <span className="detail-field-label">Description</span>
                    <textarea rows={2} value={form.description} onChange={set('description')}
                              className="detail-field-input"
                              placeholder="Short one-liner pitch"/>
                </label>
                <label className="detail-field detail-field-full">
                    <span className="detail-field-label">Financing need (€)</span>
                    <input type="number" value={form.financing_need}
                           onChange={set('financing_need')}
                           className="detail-field-input mono"
                           placeholder="e.g. 1500000"/>
                    <span className="detail-field-hint">
                        How much is this startup raising in total? One number — investors will be matched to this.
                    </span>
                </label>
                <div className="detail-field detail-field-full">
                    <span className="detail-field-label">Email subject (template)</span>
                    <input ref={subjectRef}
                           value={form.email_subject} onChange={set('email_subject')}
                           className="detail-field-input"
                           placeholder="e.g. Introducing Sunco — Series A clean energy"/>
                    <TemplatePills fieldRef={subjectRef} field="email_subject"
                                   form={form} setForm={setForm} />
                </div>
                <div className="detail-field detail-field-full">
                    <span className="detail-field-label">Email body (template)</span>
                    <textarea ref={bodyRef}
                              rows={6} value={form.email_body} onChange={set('email_body')}
                              className="detail-field-input"
                              placeholder="Dear {{contact_full_name}}, ..."/>
                    <TemplatePills fieldRef={bodyRef} field="email_body"
                                   form={form} setForm={setForm} />
                </div>
            </div>

            {error && <div className="panel-error">{error.message}</div>}

            <div className="invite-actions">
                <button type="button" className="btn-ghost" onClick={onCancel} disabled={submitting}>
                    <X size={14} strokeWidth={2}/> Cancel
                </button>
                <button type="submit" className="btn-save" disabled={submitting}>
                    {submitting ? <span className="spinner"/> : <><Check size={14} strokeWidth={2}/> Save</>}
                </button>
            </div>
        </form>
    );
}

function formatEuro(n) {
    if (n == null || n === '') return '–';
    return '€' + Number(n).toLocaleString('de-DE');
}
